/**
 * FrameUpdater - 帧更新逻辑
 *
 * 封装 update() 循环中的子步骤：LOD 加载完成检测、LOD 稳定性检测、隐藏模型缓存清理。
 */

import * as THREE from 'three'
import type { GSplatClipState } from '@/plugins/gsplat/clipping/GSplatClipState'
import type { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import type { LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import { GSplatGpuPicker } from '@/plugins/gsplat/spatial/GSplatGpuPicker'
import { GSplatPickManager } from '@/plugins/gsplat/spatial/GSplatPickManager'
import { getLoggerManager } from '@/shared/logging'
import type { LoadResult } from '@/shared/types/gsplat'
import { convertBox3ToBoundingBox } from './ModelLoader'
import type { ModelRegistry } from './ModelRegistry'

/** 帧更新 logger（命名空间 gsplat），firstFrame 埋点经 log.perf 输出 */
const log = getLoggerManager().getLogger('gsplat')

/** FrameUpdater 依赖的上下文 */
export interface FrameUpdaterContext {
  /** 当前 WebGL 渲染器 */
  renderer: THREE.WebGLRenderer
  /** 模型注册表 */
  registry: ModelRegistry
  /** 取当前 Director，尚未就绪返回 null */
  getDirector: () => GSplatDirector | null
  /** 取当前 LayerComposition，尚未就绪返回 null */
  getComposition: () => LayerComposition | null
  /** 确保相机已注册到 composition */
  ensureCameraRegistered: (camera: THREE.Camera) => void
  /** 按 nodeId 移除模型 */
  removeByNodeId: (nodeId: string) => boolean
  /** 取当前 unified LOD polygon 裁切状态 */
  getClipState?: () => GSplatClipState
}

interface ManagerStabilityState {
  lastVersion: number
  stableFrames: number
}

interface LodStabilityTracker {
  managers: Map<GSplatManager, ManagerStabilityState>
  hasObservedManager: boolean
}

/** 帧更新管理器 */
export class FrameUpdater {
  /** 上一次使用的相机 */
  private _lastCamera: THREE.Camera | null = null

  /** 强制使用 CPU 拾取的节点 ID 集合 */
  readonly forceCpuPickNodeIds: Set<string> = new Set()

  /** 显示切换时按 nodeId 与 Manager 隔离的 LOD 版本稳定性追踪 */
  private lodStabilityTracker: Map<string, LodStabilityTracker> = new Map()

  /** 隐藏模型的缓存队列 */
  readonly hiddenModelsCache: string[] = []

  /** 帧计数器 */
  private frameCounter = 0

  /** 未接入 EngineFrameContext 的直接调用所使用的逻辑帧计数器。 */
  private logicalFrameId = 0

  /** GPU Picker 预热状态追踪 */
  private gpuPickerLastPreparedKey = ''
  private gpuPickerLastSeenKey = ''
  private gpuPickerStableFrames = 0
  private gpuPickerPreheatPending = false

  /** 缓存检查间隔（帧数） */
  private readonly CACHE_CHECK_INTERVAL = 1800

  /** 隐藏模型缓存阈值 */
  private hiddenModelsCacheLimit = 1

  /** firstFrame 计时 end 函数，首个 update 时创建，首个 LOD 加载完成时调用 */
  private _firstFrameEnd: (() => number) | null = null
  /** 是否已启动 firstFrame 计时 */
  private _firstFrameStarted = false

  constructor(private ctx: FrameUpdaterContext) {}

  /** 获取上一次使用的相机 */
  get lastCamera(): THREE.Camera | null {
    return this._lastCamera
  }

  /**
   * 每帧更新。
   *
   * @param camera - 当前相机，用于驱动 LOD / 拾取等子系统
   * @param delta - 与上一帧的时间差，单位秒；大于 0 时推进 3DGS shader effect
   */
  update(camera: THREE.Camera, frameId?: number, frameSource?: object): void {
    // 首个 update：启动 firstFrame 计时（perf 关闭时为 no-op）
    if (!this._firstFrameStarted) {
      this._firstFrameStarted = true
      this._firstFrameEnd = log.perf.time('firstFrame')
    }

    // 更新 GPU Picker 全局帧计数器
    GSplatGpuPicker.incrementFrameId()

    this._lastCamera = camera
    camera.updateMatrixWorld(true)

    this.ctx.ensureCameraRegistered(camera)

    // 更新 Director
    const director = this.ctx.getDirector()
    const composition = this.ctx.getComposition()
    if (director && composition) {
      const logicalFrameId =
        typeof frameId === 'number' && Number.isFinite(frameId)
          ? Math.floor(frameId)
          : ++this.logicalFrameId
      director.update(composition, logicalFrameId, frameSource ?? this)
    }

    // 检测 LOD 模型加载完成状态
    this.checkLODLoadingComplete()

    // 检测显示切换后的 LOD 稳定性
    this.checkLODStability()

    // 空闲时预热 GPU Picker（排序稳定 3 帧后执行）
    this.tryPreheatGpuPicker(camera)

    // 定期检查隐藏模型缓存
    this.frameCounter++
    if (this.frameCounter >= this.CACHE_CHECK_INTERVAL) {
      this.frameCounter = 0
      this.checkAndCleanHiddenModelsCache()
    }
  }

  updateShaderEffects(delta = 0): void {
    for (const entry of this.ctx.registry.singleFiles.values()) {
      entry.mesh.updateShaderEffect(delta)
    }

    for (const entry of this.ctx.registry.lodGroups.values()) {
      entry.renderMesh.updateShaderEffect(delta)
    }
  }

  /**
   * 初始化 LOD 稳定性追踪。
   *
   * @param nodeId - 模型唯一标识
   * @param manager - 已存在的 GSplatManager；尚未创建实例时可省略
   */
  initStabilityTracker(nodeId: string, manager?: GSplatManager): void {
    let tracker = this.lodStabilityTracker.get(nodeId)
    if (!tracker) {
      tracker = {
        managers: new Map(),
        hasObservedManager: false,
      }
      this.lodStabilityTracker.set(nodeId, tracker)
    }

    if (manager) {
      tracker.managers.set(manager, {
        lastVersion: manager.sortedVersion,
        stableFrames: 0,
      })
      tracker.hasObservedManager = true
      this.forceCpuPickNodeIds.add(nodeId)
    }
  }

  /** 是否存在需要连续帧推进的 LOD 稳定性追踪。 */
  hasStabilityTrackingWork(): boolean {
    for (const tracker of this.lodStabilityTracker.values()) {
      if (tracker.managers.size > 0) return true
    }
    return false
  }

  /**
   * 清理指定 nodeId 的强制 CPU 拾取和稳定性追踪。
   *
   * @param nodeId - 模型唯一标识
   */
  clearTracker(nodeId: string): void {
    this.forceCpuPickNodeIds.delete(nodeId)
    this.lodStabilityTracker.delete(nodeId)
  }

  /** 销毁 */
  dispose(): void {
    this._lastCamera = null
    this.forceCpuPickNodeIds.clear()
    this.lodStabilityTracker.clear()
    this.hiddenModelsCache.length = 0
    this.frameCounter = 0
    this.logicalFrameId = 0
    this.gpuPickerLastPreparedKey = ''
    this.gpuPickerLastSeenKey = ''
    this.gpuPickerStableFrames = 0
    this.gpuPickerPreheatPending = false
  }

  // ==================== 私有方法 ====================

  /** 检测 LOD 模型加载完成状态 */
  private checkLODLoadingComplete(): void {
    const director = this.ctx.getDirector()
    if (!director || !this._lastCamera) return

    for (const [, entry] of this.ctx.registry.lodGroups.entries()) {
      if (!entry.onLoadCompleteCallback || entry.loadCompleteCallbackFired) continue

      const manager = director.getManager(this._lastCamera, entry.layer)
      if (!manager) continue

      const currentLoadingCount = manager.loadingCount

      if (entry.lastLoadingCount === undefined) {
        if (currentLoadingCount === 0) {
          this.fireLODLoadComplete(entry)
        } else {
          entry.lastLoadingCount = currentLoadingCount
        }
        continue
      }

      if (entry.lastLoadingCount > 0 && currentLoadingCount === 0) {
        this.fireLODLoadComplete(entry)
      }

      entry.lastLoadingCount = currentLoadingCount
    }
  }

  /** 触发 LOD 加载完成回调 */
  private fireLODLoadComplete(entry: import('./types').LODGroupEntry): void {
    const result: LoadResult = {
      type: 'lod',
      id: entry.nodeId,
      boundingBox: convertBox3ToBoundingBox(entry.boundingBox),
      numSplats: entry.totalPointCount,
      environmentUrl: entry.octree.environmentUrl,
      environmentConfigured: entry.octree.environmentUrl !== null,
      _raw: entry,
    }

    try {
      entry.onLoadCompleteCallback!(result)
    } catch (error) {
      log.error(
        '[FrameUpdater] onLoadComplete 回调执行出错: ' +
          (error instanceof Error ? error.message : String(error))
      )
    }

    // 首个 LOD 模型加载完成：结束 firstFrame 计时
    if (this._firstFrameEnd) {
      this._firstFrameEnd()
      this._firstFrameEnd = null
    }

    entry.loadCompleteCallbackFired = true
  }

  /** 检测显示切换后的 LOD 稳定性 */
  private checkLODStability(): void {
    const director = this.ctx.getDirector()
    if (!director) return

    for (const [nodeId, tracker] of Array.from(this.lodStabilityTracker.entries())) {
      const clientId = this.ctx.registry.resolveClientId(nodeId)
      if (!clientId) {
        this.clearTracker(nodeId)
        continue
      }

      const lodEntry = this.ctx.registry.lodGroups.get(clientId)
      if (!lodEntry) {
        this.clearTracker(nodeId)
        continue
      }

      const activeManagers = director
        .getAllManagers()
        .filter((manager) => manager.octreeInstances.has(lodEntry.placement))
      const activeManagerSet = new Set(activeManagers)

      for (const manager of Array.from(tracker.managers.keys())) {
        if (!activeManagerSet.has(manager)) {
          tracker.managers.delete(manager)
        }
      }

      for (const manager of activeManagers) {
        if (!tracker.managers.has(manager)) {
          tracker.managers.set(manager, {
            lastVersion: manager.sortedVersion,
            stableFrames: 0,
          })
        }
      }

      if (activeManagers.length === 0) {
        if (tracker.hasObservedManager) {
          this.clearTracker(nodeId)
        }
        continue
      }
      tracker.hasObservedManager = true
      this.forceCpuPickNodeIds.add(nodeId)

      let allStable = true
      for (const manager of activeManagers) {
        const managerTracker = tracker.managers.get(manager)!
        const currentVersion = manager.sortedVersion

        if (currentVersion === managerTracker.lastVersion && manager.loadingCount === 0) {
          managerTracker.stableFrames++
        } else {
          managerTracker.lastVersion = currentVersion
          managerTracker.stableFrames = 0
        }

        if (managerTracker.stableFrames < 60) {
          allStable = false
        }
      }

      // GPU Picker 预热由 tryPreheatGpuPicker() 统一管理，此处不再调用 prepare()。
      if (allStable) {
        this.clearTracker(nodeId)
      }
    }
  }

  /**
   * 空闲时预热 GPU Picker
   *
   * pickStateVersion 连续 3 帧不变后执行 prepare，使后续 pick 直接走 readPixels（<5ms）。
   * version 变化来源包括 LOD 排序和标量过滤变更，均会触发重新预热。
   */
  private tryPreheatGpuPicker(camera: THREE.Camera): void {
    const director = this.ctx.getDirector()
    if (!director || !this.ctx.renderer) return

    // 收集所有有活跃 splats 的 manager 及其 splatInfos
    const allSplatInfos: import('@/plugins/gsplat/lod/GSplatInfo').GSplatInfo[] = []
    let primaryManager: import('@/plugins/gsplat/lod/GSplatManager').GSplatManager | null = null
    let combinedVersion = 0

    for (const [, entry] of this.ctx.registry.lodGroups.entries()) {
      const m = director.getManager(camera, entry.layer)
      if (!m) {
        continue
      }
      const infos = m.getActiveSplatInfos()
      if (infos.length === 0) {
        continue
      }
      if (!primaryManager) {
        primaryManager = m
      }
      allSplatInfos.push(...infos)
      combinedVersion += m.pickStateVersion
    }

    if (!primaryManager || allSplatInfos.length === 0) {
      return
    }

    const clipStateVersion = this.ctx.getClipState?.().version ?? 0
    const preheatKey = this.buildGpuPickerPreheatKey(camera, combinedVersion, clipStateVersion)

    if (preheatKey === this.gpuPickerLastPreparedKey) {
      return
    }

    // 检查 version 是否连续稳定
    if (preheatKey !== this.gpuPickerLastSeenKey) {
      this.gpuPickerLastSeenKey = preheatKey
      this.gpuPickerStableFrames = 0
      return
    }

    this.gpuPickerStableFrames++
    if (this.gpuPickerStableFrames < 3 || this.gpuPickerPreheatPending) {
      return
    }

    // 稳定 3 帧，异步执行预热（不阻塞当前渲染帧）
    this.gpuPickerPreheatPending = true
    const keySnapshot = preheatKey
    const renderer = this.ctx.renderer
    const cameraRef = camera
    const managerRef = primaryManager
    const splatInfosRef = allSplatInfos
    const clipStateVersionSnapshot = clipStateVersion

    setTimeout(() => {
      this.gpuPickerPreheatPending = false
      // 版本已过期则放弃本次预热
      if (this.gpuPickerLastSeenKey !== keySnapshot) {
        return
      }
      try {
        const gpuPicker = GSplatPickManager['getGpuPicker'](renderer)
        gpuPicker.prepare(cameraRef, managerRef, splatInfosRef)
        GSplatPickManager.markGpuPickerPrepared(managerRef, clipStateVersionSnapshot)
        this.gpuPickerLastPreparedKey = keySnapshot
        this.gpuPickerStableFrames = 0
      } catch (error) {
        log.warn(
          '[FrameUpdater] GPU Picker 预热失败: ' +
            (error instanceof Error ? error.message : String(error))
        )
      }
    }, 0)
  }

  private buildGpuPickerPreheatKey(
    camera: THREE.Camera,
    combinedVersion: number,
    clipStateVersion: number
  ): string {
    camera.updateMatrixWorld(true)
    const size = this.ctx.renderer.getSize(new THREE.Vector2())
    return `${combinedVersion}|clip:${clipStateVersion}|${size.x}x${size.y}|${this.serializeMatrix(camera.matrixWorld)}|${this.serializeMatrix(camera.projectionMatrix)}`
  }

  private serializeMatrix(matrix: THREE.Matrix4): string {
    return matrix.elements.map((value) => Math.round(value * 1e6)).join(',')
  }

  /** 检查并清理隐藏模型缓存 */
  private checkAndCleanHiddenModelsCache(): void {
    if (this.hiddenModelsCache.length === 0) return

    while (this.hiddenModelsCache.length > this.hiddenModelsCacheLimit) {
      const nodeId = this.hiddenModelsCache.shift()
      if (!nodeId) break

      const clientId = this.ctx.registry.resolveClientId(nodeId)
      if (!clientId) continue

      const lodEntry = this.ctx.registry.lodGroups.get(clientId)
      if (lodEntry && !lodEntry.rootNode.visible) {
        this.ctx.removeByNodeId(nodeId)
      }
    }
  }
}
